import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { useTranslation } from 'react-i18next';
import { INCOME_TAX_DECLARATION_SHOW } from 'src/constants/EndpointUrls';
import { FORM_NAME_INCOME_TAX_DECLARATIONS_SEARCH } from 'src/reducers/searchForm';
import { toggleCheckDeclaration, toggleCheckAllDeclarations, getCheckedDeclarations } from 'src/reducers/reports/incomeTaxDeclarations';
import SortableTh from 'src/components/SortableTh';

import IncomeTaxDeclarationTableRow from './IncomeTaxDeclarationTableRow';

const IncomeTaxDeclarationTable = ({ declarations, mode }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const checkedDeclarations = useSelector(getCheckedDeclarations);

  const checkedIdsSet = useMemo(() => new Set(checkedDeclarations.map(d => d.id)), [checkedDeclarations]);

  const handleRowClick = declarationId => {
    const path = INCOME_TAX_DECLARATION_SHOW.replace(':id', declarationId);
    dispatch(push(path));
  };

  const handleCheckboxChange = declaration => {
    dispatch(toggleCheckDeclaration(declaration));
  };

  const handleCheckAllChange = () => {
    dispatch(toggleCheckAllDeclarations(declarations));
  };

  const allChecked = declarations.length > 0 && declarations.every(d => checkedIdsSet.has(d.id));

  return (
    <div className="l-overflow-scroll">
      <table className="m-table-list">
        <thead>
          <tr>
            {mode === 'EDIT' && (
              <th width={60}>
                <input type="checkbox" checked={allChecked} onChange={handleCheckAllChange} />
              </th>
            )}
            <SortableTh field="declarationName" formName={FORM_NAME_INCOME_TAX_DECLARATIONS_SEARCH}>
              {t('features.ReportIncomeTax.list.table.headers.declaration_name')}
            </SortableTh>
            <SortableTh field="clientBranchId" formName={FORM_NAME_INCOME_TAX_DECLARATIONS_SEARCH}>
              {t('features.ReportIncomeTax.list.table.headers.office')}
            </SortableTh>
            <SortableTh field="formCode" formName={FORM_NAME_INCOME_TAX_DECLARATIONS_SEARCH}>
              {t('features.ReportIncomeTax.list.table.headers.form')}
            </SortableTh>
            <SortableTh field="periodYear" formName={FORM_NAME_INCOME_TAX_DECLARATIONS_SEARCH}>
              {t('features.ReportIncomeTax.list.table.headers.tax_period')}
            </SortableTh>
            <SortableTh field="exportedAt" formName={FORM_NAME_INCOME_TAX_DECLARATIONS_SEARCH}>
              {t('features.ReportIncomeTax.list.table.headers.last_exported')}
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {(!declarations || declarations.length === 0) && (
            <tr>
              <td colSpan={mode === 'EDIT' ? 6 : 5}>{t('features.ReportIncomeTax.list.table.no_data')}</td>
            </tr>
          )}

          {declarations &&
            declarations.map(declaration => (
              <IncomeTaxDeclarationTableRow
                key={declaration.id}
                declaration={declaration}
                onClick={() => handleRowClick(declaration.id)}
                mode={mode}
                isChecked={checkedIdsSet.has(declaration.id)}
                onCheckboxChange={() => handleCheckboxChange(declaration)}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
};

IncomeTaxDeclarationTable.propTypes = {
  declarations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      declarationName: PropTypes.string,
      office: PropTypes.string,
      formCode: PropTypes.string,
      periodType: PropTypes.string,
      periodMonth: PropTypes.number,
      periodYear: PropTypes.number,
      periodQuarter: PropTypes.number,
      exportedAt: PropTypes.string
    })
  ),
  mode: PropTypes.string
};

IncomeTaxDeclarationTable.defaultProps = {
  declarations: [],
  mode: 'SHOW'
};

export default IncomeTaxDeclarationTable;
